import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

import template from "./LogsTemplate.html";
import { _lcs, locale } from "../../i18n";
import { gridLanguage } from "../../core/grid";

// Log levels (mirrors the CHECK constraint on logs."system"."level"). The values
// are German source strings and double as dictionary keys — this is a module
// constant, evaluated before I18n exists, so it is translated where it is read.
const LEVEL_LABELS = {
    debug    : "Debug",
    info     : "Info",
    warning  : "Warnung",
    error    : "Fehler",
    critical : "Kritisch"
};

/**
 * Logs — the SaaS-Admin platform log viewer. Two tabs, each a GridJS table:
 *   • Systemprotokoll  → logs."system"        (GET /logs/system)
 *   • API-Anfragen     → logs."apiRequestLog" (GET /logs/api-request)
 *
 * Each tab's data is fetched lazily: only the initially visible tab loads on
 * enter; the other loads the first time it is activated (and is then cached).
 */
export class Logs extends InAppState
{
    static ROUTE = '/admin/logs';
    static ID = 'admin-logs';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;

        this.tabs = Array.from( root.querySelectorAll( ".logs__tab" ) );
        this.panels = {
            system : root.querySelector( '[data-panel="system"]' ),
            api    : root.querySelector( '[data-panel="api"]' )
        };
        this.stateEls = {
            system : root.querySelector( '[data-id="system-state"]' ),
            api    : root.querySelector( '[data-id="api-state"]' )
        };
        this.grids = { system: null, api: null };
        this.loaded = { system: false, api: false };
        this.activeTab = "system";

        // Tracked listeners — InfrontJS removes them automatically on state exit.
        this.tabs.forEach( ( tab ) =>
            this.addEventListener( tab, "click", () => this.switchTab( tab.getAttribute( "data-tab" ) ) )
        );

        // Only the initially visible tab is loaded now.
        await this.switchTab( "system" );
    }

    async switchTab( key )
    {
        if ( !this.panels[ key ] ) return;

        this.activeTab = key;

        this.tabs.forEach( ( tab ) =>
        {
            const on = tab.getAttribute( "data-tab" ) === key;
            tab.classList.toggle( "logs__tab--active", on );
            tab.setAttribute( "aria-selected", String( on ) );
        } );

        Object.keys( this.panels ).forEach( ( k ) =>
        {
            this.panels[ k ].hidden = ( k !== key );
        } );

        // Lazy load: fetch a tab's data only the first time it is shown.
        if ( !this.loaded[ key ] )
        {
            this.loaded[ key ] = true;
            try
            {
                if ( key === "system" ) await this.loadSystem();
                else                    await this.loadApi();
            }
            catch ( e )
            {
                // Allow a retry on the next activation.
                this.loaded[ key ] = false;
                throw e;
            }
        }
    }

    /* ------------------------------- Loading -------------------------------- */

    async loadSystem()
    {
        this.showMessage( "system", "scroll-text", _lcs( "Lade Systemprotokoll …" ), _lcs( "Einen Moment bitte." ) );

        let rows = [];
        try
        {
            const { result } = await this.app.api.get( "/logs/system" );
            if ( !result || +result.status !== 200 || !Array.isArray( result.data ) )
            {
                throw new Error( "Unexpected /logs/system response." );
            }
            rows = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "system",
                "triangle-alert",
                _lcs( "Protokoll konnte nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        if ( rows.length === 0 )
        {
            this.showMessage( "system", "scroll-text",
                _lcs( "Keine Einträge" ), _lcs( "Es liegen keine Systemprotokoll-Einträge vor." ) );
            return;
        }

        this.renderSystemGrid( rows );
    }

    async loadApi()
    {
        this.showMessage( "api", "activity", _lcs( "Lade API-Anfragen …" ), _lcs( "Einen Moment bitte." ) );

        let rows = [];
        try
        {
            const { result } = await this.app.api.get( "/logs/api-request" );
            if ( !result || +result.status !== 200 || !Array.isArray( result.data ) )
            {
                throw new Error( "Unexpected /logs/api-request response." );
            }
            rows = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "api",
                "triangle-alert",
                _lcs( "Protokoll konnte nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        if ( rows.length === 0 )
        {
            this.showMessage( "api", "activity",
                _lcs( "Keine Einträge" ), _lcs( "Es liegen keine API-Anfragen vor." ) );
            return;
        }

        this.renderApiGrid( rows );
    }

    /* ------------------------------- Grids ---------------------------------- */

    renderSystemGrid( entries )
    {
        const el = this.stateEls.system;
        el.innerHTML = "";

        const rows = entries.map( ( r ) => [
            this._formatDate( r._createdAt ),
            String( r.level || "info" ),
            String( r.message == null ? "" : r.message ),
            r.data == null ? "" : ( typeof r.data === "string" ? r.data : JSON.stringify( r.data ) )
        ] );

        this.grids.system = new Grid( {
            columns: [
                { name: _lcs( "Zeitpunkt" ), width: "170px" },
                {
                    name: _lcs( "Level" ),
                    width: "110px",
                    formatter: ( cell ) => html( this._levelBadgeHtml( cell ) )
                },
                {
                    name: _lcs( "Nachricht" ),
                    formatter: ( cell ) => html(
                        '<span class="logs__cell" title="' + this._escape( cell ) + '">' + this._escape( cell ) + "</span>"
                    )
                },
                {
                    name: _lcs( "Daten" ),
                    formatter: ( cell ) => html(
                        '<span class="logs__cell logs__cell--mono" title="' + this._escape( cell ) + '">' +
                            this._escape( cell ) +
                        "</span>"
                    )
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 15, summary: true },
            className: { container: "admin-users__grid logs__grid" },
            language: gridLanguage( _lcs( "Einträgen" ) )
        } );

        this.grids.system.render( el );
        this._mountTruncateButton( "system" );
    }

    renderApiGrid( entries )
    {
        const el = this.stateEls.api;
        el.innerHTML = "";

        const rows = entries.map( ( r ) => [
            this._formatDate( r.timestamp ),
            String( r.method || "" ),
            String( r.route || r.path || "" ),
            r.statusCode == null ? "" : String( r.statusCode ),
            r.durationMs == null ? "" : String( r.durationMs ),
            r.userId == null ? "" : String( r.userId ),
            r.errorMessage == null ? "" : String( r.errorMessage )
        ] );

        this.grids.api = new Grid( {
            columns: [
                { name: _lcs( "Zeitpunkt" ), width: "170px" },
                { name: _lcs( "Methode" ), width: "90px" },
                {
                    name: _lcs( "Pfad" ),
                    formatter: ( cell ) => html(
                        '<span class="logs__cell logs__cell--mono" title="' + this._escape( cell ) + '">' +
                            this._escape( cell ) +
                        "</span>"
                    )
                },
                {
                    name: _lcs( "Status" ),
                    width: "90px",
                    formatter: ( cell ) => html( this._statusBadgeHtml( cell ) )
                },
                {
                    name: _lcs( "Dauer" ),
                    width: "100px",
                    formatter: ( cell ) => html( cell ? this._escape( cell ) + " ms" : "—" )
                },
                {
                    name: _lcs( "Benutzer" ),
                    formatter: ( cell ) => html(
                        '<span class="logs__cell logs__cell--mono" title="' + this._escape( cell ) + '">' +
                            ( cell ? this._escape( cell ) : "—" ) +
                        "</span>"
                    )
                },
                {
                    name: _lcs( "Fehler" ),
                    formatter: ( cell ) => html(
                        '<span class="logs__cell" title="' + this._escape( cell ) + '">' +
                            ( cell ? this._escape( cell ) : "—" ) +
                        "</span>"
                    )
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 15, summary: true },
            className: { container: "admin-users__grid logs__grid" },
            language: gridLanguage( _lcs( "Anfragen" ) )
        } );

        this.grids.api.render( el );
        this._mountTruncateButton( "api" );
    }

    /* ------------------------------ Truncate -------------------------------- */

    // Metadata per tab: API endpoint + human label, used by truncate / reload.
    _tabMeta( key )
    {
        return key === "system"
            ? { endpoint: "/logs/system",      label: _lcs( "Systemprotokoll" ), reload: () => this.loadSystem() }
            : { endpoint: "/logs/api-request", label: _lcs( "API-Anfragen" ),    reload: () => this.loadApi() };
    }

    /**
     * Inject the "Alle löschen" button into the GridJS head row (same row as the
     * "Suchen …" input), floated to the right via .logs__truncate. Only present
     * when a grid is actually rendered (i.e. the table has entries).
     */
    _mountTruncateButton( key )
    {
        const head = this.stateEls[ key ].querySelector( ".gridjs-head" );
        if ( !head ) return;

        // Wrap the button in its own container that is pinned to the right edge
        // of the head row (see logs.css). This keeps it flush-right next to the
        // "Suchen …" field regardless of GridJS's float-based head layout.
        const actions = document.createElement( "div" );
        actions.className = "logs__head-actions";

        const btn = document.createElement( "button" );
        btn.type = "button";
        btn.className = "logs__truncate admin-users__action admin-users__action--danger";
        btn.innerHTML = '<i class="icon icon--trash-2"></i><span>' + this._escape( _lcs( "Alle löschen" ) ) + '</span>';

        this.addEventListener( btn, "click", () => this.onTruncate( key ) );
        actions.appendChild( btn );
        head.appendChild( actions );
    }

    async onTruncate( key )
    {
        const meta = this._tabMeta( key );

        const ok = await this.app.view.confirm(
            _lcs( "Alle Einträge löschen?" ),
            _lcs( "Möchtest du wirklich alle Einträge aus „{log}“ unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.", { log: meta.label } ),
            _lcs( "Alle löschen" ),
            _lcs( "Abbrechen" ),
            { danger: true }
        );
        if ( !ok ) return;

        try
        {
            const { result } = await this.app.api.delete( meta.endpoint );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected truncate response." );
            }

            // Reload the (now-empty) tab and confirm.
            await meta.reload();
            this.app.view.toast( _lcs( "Alle Einträge aus „{log}“ wurden gelöscht.", { log: meta.label } ), "success" );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Löschen fehlgeschlagen. Bitte versuche es später erneut." ), "error" );
        }
    }

    /**
     * Render a centered icon + heading + text block (loading / empty / error)
     * into a tab's state container, reusing the shared .content__empty styling.
     */
    showMessage( key, icon, title, text )
    {
        const el = this.stateEls[ key ];
        if ( this.grids[ key ] )
        {
            try { this.grids[ key ].destroy(); } catch ( e ) { /* noop */ }
            this.grids[ key ] = null;
        }
        el.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                "<h2></h2>" +
                "<p></p>" +
            "</div>";
        // textContent assignment avoids HTML injection from dynamic strings.
        el.querySelector( "h2" ).textContent = title;
        el.querySelector( "p" ).textContent = text;
    }

    async exit()
    {
        [ "system", "api" ].forEach( ( key ) =>
        {
            if ( this.grids && this.grids[ key ] )
            {
                try { this.grids[ key ].destroy(); } catch ( e ) { /* DOM is cleared below anyway */ }
                this.grids[ key ] = null;
            }
        } );
        // Base InAppState clears the page-content container.
        await super.exit();
    }

    /* ------------------------------- Helpers -------------------------------- */

    _levelBadgeHtml( level )
    {
        const key = String( level || "info" ).toLowerCase();
        const known = LEVEL_LABELS.hasOwnProperty( key );
        const label = known ? this._escape( _lcs( LEVEL_LABELS[ key ] ) ) : this._escape( level );
        const mod = known ? key : "info";
        return '<span class="log-badge log-badge--' + mod + '">' + label + "</span>";
    }

    _statusBadgeHtml( status )
    {
        const code = parseInt( status, 10 );
        if ( isNaN( code ) )
        {
            return '<span class="status-badge">—</span>';
        }
        let mod = "ok";
        if ( code >= 500 )      mod = "err";
        else if ( code >= 400 ) mod = "warn";
        return '<span class="status-badge status-badge--' + mod + '">' + code + "</span>";
    }

    _escape( str )
    {
        return String( str == null ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    _formatDate( value )
    {
        if ( !value )
        {
            return "—";
        }
        const d = new Date( value );
        if ( isNaN( d.getTime() ) )
        {
            return String( value );
        }
        return new Intl.DateTimeFormat( locale(), {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit"
        } ).format( d );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Protokolle" ),
            subtitle: _lcs( "System- und API-Protokolle" )
        };
    }
}
